import Search from "@arcgis/core/widgets/Search";
import { AppDispatch } from "../../../state-management/store";
import MapView from "@arcgis/core/views/MapView";
import { LoggingService } from "../../../shared/services/Logging.service";
import { ELogTypes } from "../../../shared/enums/logging.enums";
import { SearchSelectResultEvent } from "../Map.types";
import { consoleSuccessStyle } from "../../../shared/constants/logging.constants";

/**
 * @export
 * @class SearchWidget
 */
export class SearchWidget {
  private static instance: SearchWidget;
  private searchWidget!: Search;
  private mapView: MapView;
  private loggingService: LoggingService;
  private dispatch: AppDispatch;
  private sendDataToGTM: any;

  /**
   * Creates an instance of SearchWidget.
   * @param {MapView} mapView
   * @param {AppDispatch} dispatch
   * @memberof SearchWidget
   */
  private constructor(
    mapView: MapView,
    dispatch: AppDispatch,
    sendDataToGTM: any
  ) {
    this.mapView = mapView;
    this.dispatch = dispatch;
    this.sendDataToGTM = sendDataToGTM;

    this.loggingService = LoggingService.getInstance(dispatch);
    this.buildSearchWidget();
  }

  /**
   * @private buildSearchWidget
   * @memberof SearchWidget
   */
  private buildSearchWidget(): void {
    this.searchWidget = new Search({
      view: this.mapView,
      container: "searchContainer",
      minSuggestCharacters: 3,
      popupEnabled: false,
      locationEnabled: false,
      goToOverride: function (view, options) {
        options.target.scale = 1500;
        return view.goTo(options.target);
      },
    });

    // Search widget does not have a placeholder prop, so
    // this is a trick to override that.
    this.searchWidget.watch("activeSource", function (ctrl) {
      ctrl.placeholder = "Search for a location";
    });

    this.searchWidget.on("select-result", (event: SearchSelectResultEvent) => {
      const { result } = event as any;
      const resultData = {
        name: result.name,
        feature: result.feature.toJSON(),
        extent: result.extent.toJSON(),
      };

      this.loggingService.logMessage({
        type: ELogTypes.SEARCH,
        message: `Search Query: ${result.name}`,
        data: resultData,
      });

      this.sendDataToGTM({
        event: "eventTracking",
        category: "Outage",
        action: "Street Light",
        label: `Search query executed ${result.name}`,
      });
    });

    this.mapView.ui.add(this.searchWidget, {
      position: "top-left",
      index: 300,
    });

    // eslint-disable-next-line no-console
    console.info("%cSearch Widget Initialized", consoleSuccessStyle);
  }

  /**
   * @static getInstance
   * @param {MapView} mapView
   * @param {AppDispatch} dispatch
   * @return {*}  {SearchWidget}
   * @memberof SearchWidget
   */
  public static getInstance(
    mapView: MapView,
    dispatch: AppDispatch,
    sendDataToGTM: any
  ): SearchWidget {
    if (!SearchWidget.instance) {
      SearchWidget.instance = new SearchWidget(
        mapView,
        dispatch,
        sendDataToGTM
      );
    }
    return SearchWidget.instance;
  }

  /**
   * getSearchWidget
   * @return {*}  {Search}
   * @memberof SearchWidget
   */
  public getSearchWidget(): Search {
    return this.searchWidget;
  }
}
